import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownMessageProps {
  content: string
  className?: string
}

/**
 * Composant qui rend du Markdown dans les messages de chat.
 * Design system: Charte emerald avec hiérarchie visuelle claire
 * Gère : tableaux, listes, code, blockquotes, headings, liens
 */
export default function MarkdownMessage({ content, className = '' }: MarkdownMessageProps) {
  return (
    <div className={`markdown-message ${className}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Liens - Emerald avec effet hover subtil
        a: ({ href, children, ...props }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline decoration-emerald-500/40 underline-offset-2 transition-colors font-medium break-words"
            onClick={(e) => e.stopPropagation()}
            {...props}
          >
            {children}
          </a>
        ),
        
        // Paragraphes - Espacement cohérent
        p: ({ children, ...props }) => (
          <p className="mb-3 last:mb-0 leading-relaxed text-zinc-700 dark:text-zinc-300" {...props}>{children}</p>
        ),
        
        // Gras - Accent emerald subtil
        strong: ({ children, ...props }) => (
          <strong className="font-semibold text-zinc-900 dark:text-zinc-100" {...props}>{children}</strong>
        ),
        
        // Italique
        em: ({ children, ...props }) => (
          <em className="italic text-zinc-600 dark:text-zinc-400" {...props}>{children}</em>
        ),
        
        // Listes à puces - Design moderne avec bullets emerald
        ul: ({ children, ...props }) => (
          <ul className="mb-3 last:mb-0 space-y-2 pl-1" {...props}>{children}</ul>
        ),
        
        // Listes numérotées - Compteur emerald
        ol: ({ children, ...props }) => (
          <ol className="mb-3 last:mb-0 space-y-2 pl-1" {...props}>{children}</ol>
        ),
        
        // Items de liste - Bullet emerald custom
        li: ({ children, ...props }) => (
          <li className="flex items-start gap-2 leading-relaxed text-zinc-700 dark:text-zinc-300" {...props}>
            <span className="mt-1.5 flex h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 dark:bg-emerald-400" aria-hidden />
            <span className="flex-1">{children}</span>
          </li>
        ),
        
        // Headings - Hiérarchie visuelle claire avec accent emerald
        h1: ({ children, ...props }) => (
          <h1 className="mb-3 mt-4 first:mt-0 text-lg font-bold text-zinc-900 dark:text-zinc-100 border-b-2 border-emerald-500/30 pb-2" {...props}>
            {children}
          </h1>
        ),
        h2: ({ children, ...props }) => (
          <h2 className="mb-2.5 mt-3 first:mt-0 text-base font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2" {...props}>
            <span className="h-1 w-1 rounded-full bg-emerald-500" aria-hidden />
            {children}
          </h2>
        ),
        h3: ({ children, ...props }) => (
          <h3 className="mb-2 mt-2.5 first:mt-0 text-sm font-semibold text-emerald-700 dark:text-emerald-300" {...props}>
            {children}
          </h3>
        ),
        h4: ({ children, ...props }) => (
          <h4 className="mb-1.5 mt-2 first:mt-0 text-sm font-semibold text-zinc-700 dark:text-zinc-400" {...props}>
            {children}
          </h4>
        ),
        
        // Code inline - Badge emerald subtil
        code: ({ children, className: codeClassName, ...props }) => {
          const isInline = !codeClassName
          return isInline ? (
            <code 
              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-xs font-mono text-emerald-700 dark:text-emerald-300 border border-emerald-200/50 dark:border-emerald-800/50" 
              {...props}
            >
              {children}
            </code>
          ) : (
            <pre className="mb-3 overflow-hidden rounded-lg border border-emerald-200/50 dark:border-emerald-900/50 bg-gradient-to-br from-zinc-50 to-emerald-50/30 dark:from-zinc-900 dark:to-emerald-950/20">
              <code 
                className={`block overflow-x-auto p-4 text-xs font-mono leading-relaxed text-zinc-800 dark:text-zinc-200 ${codeClassName || ''}`} 
                {...props}
              >
                {children}
              </code>
            </pre>
          )
        },
        
        // Blockquotes - Card design avec accent emerald
        blockquote: ({ children, ...props }) => (
          <blockquote 
            className="my-3 rounded-lg border-l-4 border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 p-3 italic text-zinc-700 dark:text-zinc-300 shadow-sm" 
            {...props}
          >
            <div className="flex items-start gap-2">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
              </svg>
              <div className="flex-1">{children}</div>
            </div>
          </blockquote>
        ),
        
        // Séparateur horizontal
        hr: ({ ...props }) => (
          <hr className="my-4 border-0 h-px bg-gradient-to-r from-transparent via-emerald-300 dark:via-emerald-700 to-transparent" {...props} />
        ),
        
        // Tables - Design moderne avec header emerald
        table: ({ children, ...props }) => (
          <div className="my-3 overflow-hidden rounded-lg border border-emerald-200/70 dark:border-emerald-900/50 shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-emerald-200/70 dark:divide-emerald-900/50 text-xs" {...props}>
                {children}
              </table>
            </div>
          </div>
        ),
        thead: ({ children, ...props }) => (
          <thead className="bg-gradient-to-r from-emerald-500 to-emerald-600 dark:from-emerald-900 dark:to-emerald-800" {...props}>
            {children}
          </thead>
        ),
        tbody: ({ children, ...props }) => (
          <tbody className="divide-y divide-emerald-100/70 dark:divide-emerald-900/30 bg-white dark:bg-zinc-900" {...props}>
            {children}
          </tbody>
        ),
        tr: ({ children, ...props }) => (
          <tr className="transition-colors hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20" {...props}>
            {children}
          </tr>
        ),
        th: ({ children, ...props }) => (
          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-white dark:text-emerald-50" {...props}>
            {children}
          </th>
        ),
        td: ({ children, ...props }) => (
          <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300 whitespace-normal" {...props}>
            {children}
          </td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  )
}
